import { Button, CircularProgress, Stack } from '@mui/material';
import { downloadUrl } from 'helpers/files.helper';
import { handlePermissionCheck } from 'helpers/global.helper';
import useAuth from 'hooks/useAuth';
import { useAtom } from 'jotai';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
    useDeleteOrderFilesClientMutation,
    useFetchOrderFilesFromClientQuery,
    useLazyDownloadAllClientFilesQuery,
    useUploadOrderFilesFromClientMutation
} from 'store/api/orders.api';
import { useFetchUserOrderFieldsVisabilityQuery } from 'store/api/system.api';
import Swal from 'sweetalert2';
import Cover from 'ui-component/Cover';
import { orderIdAtom } from 'views/pages/order/OrderManagePage';
import AnimateButton from '../extended/AnimateButton';
import { OrderFileDisplay } from './OrderFileDisplayWithAdditions';
import { isUserMountingWorker } from 'helpers/users.helper';

const OrderClientFiles = () => {
    const { user } = useAuth();
    const [orderId] = useAtom(orderIdAtom);
    const { t } = useTranslation();

    const { data: orderFiles, isFetching: isFetchingFiles } = useFetchOrderFilesFromClientQuery({ orderId: orderId! }, { skip: !orderId });
    const { data: userFieldsVisibility } = useFetchUserOrderFieldsVisabilityQuery({ userId: user?.id! }, { skip: !user });

    const [deleteFile, { isLoading: isDeleting }] = useDeleteOrderFilesClientMutation();
    const [uploadFile, { isLoading: isUploading }] = useUploadOrderFilesFromClientMutation();
    const [downloadAllFiles] = useLazyDownloadAllClientFilesQuery();

    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop: (acceptedFiles) => {
            uploadFile({ orderId: orderId!, uploadedFiles: acceptedFiles, userId: user?.id! });
        },
        noClick: true
    });

    if (!orderId) return <Cover />;

    return (
        <Stack direction={'column'} {...getRootProps()} sx={{ border: '1px dashed rgba(0,0,0,0.2)', p: 1 }}>
            <Stack gap={2} direction={{ xs: 'column', sm: 'row' }} sx={{ flexWrap: 'wrap', minHeight: 100 }}>
                <input {...getInputProps()} />
                {orderFiles &&
                    orderFiles.length > 0 &&
                    orderFiles.map((file) => {
                        return (
                            <Stack
                                gap={2}
                                direction={{ xs: 'column', sm: 'row' }}
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    height: '100%',
                                    opacity: isUploading || isFetchingFiles || isDeleting ? 0 : 1,
                                    width: { xs: '100%', sm: 'initial' }
                                }}
                            >
                                <OrderFileDisplay
                                    file={file}
                                    sx={{ width: { sx: '100%', sm: 160, md: 200 } }}
                                    handleDelete={() =>
                                        Swal.fire({
                                            title: t('pages.order_manage_page.order_files.are_you_sure_to_delete_file'),
                                            text: t('pages.order_manage_page.order_files.once_deleted_cant_recover'),
                                            icon: 'warning',
                                            showCancelButton: true
                                        }).then((result) => {
                                            if (result.isConfirmed) {
                                                deleteFile({ fileId: file.id, orderId, userId: user?.id! });
                                            }
                                        })
                                    }
                                />
                            </Stack>
                        );
                    })}
                {(isUploading || isFetchingFiles || isDeleting) && (
                    <CircularProgress
                        sx={{
                            opacity: '1',
                            position: 'absolute',
                            top: '50%',
                            left: '50%'
                        }}
                    />
                )}
            </Stack>
            <Stack
                direction={{ xs: 'row', sm: 'row' }}
                sx={{ justifyContent: { xs: 'center', sm: 'start' }, alignItems: { xs: 'center', sm: 'initial' } }}
                gap={2}
            >
                {(handlePermissionCheck(userFieldsVisibility!, 'orderAllowChange') || isUserMountingWorker(user?.role)) && (
                    <AnimateButton>
                        <Button variant={'contained'} onClick={open}>
                            {t('pages.order_manage_page.order_files.upload_a_file')}
                        </Button>
                    </AnimateButton>
                )}
                <Button
                    variant={'contained'}
                    disabled={orderFiles?.length === 0}
                    onClick={async () => {
                        const file = await downloadAllFiles({ orderId }).then((result) => result.data?.file);
                        if (file) downloadUrl({ src: file, name: 'files' });
                    }}
                >
                    {t('pages.order_manage_page.order_files.download_all')}
                </Button>
            </Stack>
        </Stack>
    );
};

export default OrderClientFiles;
